/** The closure record of a completed project.
 *
 * Two problems this replaces, both found by Brian on the first real project he opened:
 *
 * 1. A complete project simply had no closure controls — the form is hidden once status is
 *    `complete`, with nothing in its place. It read as "I cannot figure out how to close
 *    this" when the truth was "this is already closed".
 * 2. The record rendered as one unlabelled paragraph, so a closure carried over from the
 *    pilot showed as raw JSON, and a missing handoff note rendered as an empty line.
 *
 * So: say plainly that it is closed, label the fields, name a missing handoff note as
 * missing, and offer a way to fix it.
 */

import { useState } from 'react'
import { CheckCircle2, Pencil } from 'lucide-react'

import { Button, ErrorState, Field, Pill, SectionHeader, inputClass } from '@/components/ui'
import { Can } from '@/components/ui'
import { formatDate, type Project } from '@/lib/domain'
import { Permissions } from '@/lib/session'
import { useAmendClosure } from '@/features/projects/useProjects'

export function ClosureRecord({ project }: { project: Project }) {
  const amend = useAmendClosure(project.id)
  const [editing, setEditing] = useState(false)
  const [summary, setSummary] = useState(project.closure.summary ?? '')
  const [handoff, setHandoff] = useState(project.closure.handoffNote ?? '')
  const [reason, setReason] = useState('')

  const missingHandoff = !project.closure.handoffNote?.trim()
  // A closure that came across from the pilot as a JSON blob. Worth naming rather than
  // leaving the reader to work out why it looks like that.
  const looksLikeJson = Boolean(project.closure.summary?.trimStart().startsWith('{'))

  return (
    <>
      <SectionHeader
        action={
          <Can permission={Permissions.projectsClose}>
            <Button onClick={() => setEditing((value) => !value)}>
              <Pencil size={14} aria-hidden />
              {editing ? 'Cancel' : 'Amend'}
            </Button>
          </Can>
        }
      >
        Closure record
      </SectionHeader>

      <div className="card space-y-3 p-4">
        <div className="flex flex-wrap items-center gap-2">
          <CheckCircle2 size={16} style={{ color: 'var(--status-green)' }} aria-hidden />
          <span className="text-sm font-semibold text-fg">
            Closed{project.actualEndDate ? ` ${formatDate(project.actualEndDate)}` : ''}
          </span>
          {missingHandoff ? <Pill tone="amber">Handoff note missing</Pill> : null}
          {looksLikeJson ? <Pill tone="amber">Imported unformatted</Pill> : null}
        </div>

        <div>
          <span className="micro-label text-fg-muted">What happened</span>
          <p className="mt-1 whitespace-pre-wrap text-sm text-fg">
            {project.closure.summary || <span className="text-fg-muted">Not recorded.</span>}
          </p>
        </div>

        <div>
          <span className="micro-label text-fg-muted">Runbook or handoff</span>
          <p className="mt-1 whitespace-pre-wrap text-sm text-fg">
            {project.closure.handoffNote || (
              <span className="text-fg-muted">
                Not recorded. This project closed before the platform required one — amend
                it to name the document, or say in one line why none is needed.
              </span>
            )}
          </p>
        </div>

        {editing ? (
          <div className="space-y-3 border-t border-border pt-3">
            <Field label="What happened">
              <textarea
                className={inputClass}
                rows={3}
                value={summary}
                onChange={(event) => setSummary(event.target.value)}
              />
            </Field>
            <Field
              label="Runbook or handoff"
              hint="Name the document, or state in one line why none is needed."
            >
              <textarea
                className={inputClass}
                rows={2}
                value={handoff}
                onChange={(event) => setHandoff(event.target.value)}
              />
            </Field>
            <Field label="Why the record is changing" hint="Recorded in the audit trail.">
              <input
                className={inputClass}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                placeholder="Adding the handoff note missed at closure"
              />
            </Field>
            <div className="flex items-center gap-3">
              <Button
                variant="primary"
                disabled={
                  reason.trim().length < 3 ||
                  summary.trim().length < 10 ||
                  handoff.trim().length < 10 ||
                  amend.isPending
                }
                onClick={() =>
                  amend.mutate(
                    {
                      summary: summary.trim(),
                      handoff_note: handoff.trim(),
                      reason: reason.trim(),
                      row_version: project.rowVersion,
                    },
                    { onSuccess: () => { setEditing(false); setReason('') } },
                  )
                }
              >
                {amend.isPending ? 'Saving…' : 'Save the record'}
              </Button>
              <p className="text-xs text-fg-muted">
                The project stays closed — this edits the record, not the decision.
              </p>
            </div>
            {amend.isError ? <ErrorState error={amend.error} /> : null}
          </div>
        ) : null}
      </div>
    </>
  )
}
